import { useEffect, useState, useRef, RefObject } from 'react'

type Options = {
  root?: Element | null
  rootMargin?: string
  threshold?: number | number[]
  onEnter?: () => void // one-time callback when the element enters view
}

export function useNearViewport<T extends HTMLElement = HTMLElement>(
  options?: Options,
): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null)
  const [isNear, setIsNear] = useState(false)
  const callbackCalledRef = useRef(false)

  useEffect(() => {
    if (!ref.current || isNear) return
    if (typeof IntersectionObserver === 'undefined') {
      if (!callbackCalledRef.current) {
        callbackCalledRef.current = true
        setIsNear(true)
        options?.onEnter?.()
      }
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !callbackCalledRef.current) {
          setIsNear(true)
          callbackCalledRef.current = true

          options?.onEnter?.()
          observer.disconnect()
        }
      },
      {
        root: options?.root ?? null,
        rootMargin: options?.rootMargin ?? '200px',
        threshold: options?.threshold ?? 0,
      },
    )

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [isNear])

  return [ref, isNear]
}
